import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Leaf, Send, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = {
  employees: "Team Awareness Survey",
  customers: "Customer Sustainability Survey",
  suppliers: "Supplier Sustainability Survey",
};

const TYPE_DESCRIPTIONS = {
  employees: "This anonymous survey helps your employer understand how sustainability is perceived within the team.",
  customers: "Your perspective on sustainability helps this company serve you better.",
  suppliers: "This assessment helps build more transparent and responsible business partnerships.",
};

export default function SurveyPage() {
  const { surveyId } = useParams();
  const [survey, setSurvey] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const fetchSurvey = async () => {
      try {
        const { data } = await axios.get(`${API}/surveys/${surveyId}`);
        setSurvey(data);
      } catch (err) {
        toast.error("Survey not found.");
      } finally {
        setLoading(false);
      }
    };
    fetchSurvey();
  }, [surveyId]);

  const answeredCount = survey ? survey.questions.filter((q) => answers[q.id]).length : 0;
  const totalQuestions = survey ? survey.questions.length : 0;

  const handleSubmit = async () => {
    if (!survey) return;
    const unanswered = survey.questions.filter((q) => !answers[q.id]);
    if (unanswered.length > 0) {
      toast.error("Please answer all questions.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/surveys/${surveyId}/respond`, { responses: answers });
      setSubmitted(true);
    } catch (err) {
      toast.error("Could not submit response.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md"
          data-testid="survey-submitted"
        >
          <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-primary" />
          </div>
          <h2 className="text-3xl font-bold text-white mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Thank you!
          </h2>
          <p className="text-white/50 leading-relaxed">
            Your response has been recorded and will help improve sustainability insights.
          </p>
        </motion.div>
      </div>
    );
  }

  if (!survey) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <p className="text-muted-foreground">Survey not found.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="survey-page">
      <div className="max-w-2xl mx-auto px-4 md:px-8 py-8 md:py-16">
        {/* Header */}
        <div className="flex items-center gap-2 mb-8">
          <Leaf className="w-5 h-5 text-primary" />
          <span className="text-lg font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Olana</span>
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <p className="text-sm text-muted-foreground uppercase tracking-widest mb-2">Survey</p>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {survey.title || TYPE_LABELS[survey.survey_type] || "Sustainability Survey"}
          </h1>
          <p className="text-muted-foreground mb-2">
            {survey.intro || TYPE_DESCRIPTIONS[survey.survey_type] || "Your honest answers help build a clearer sustainability picture."}
          </p>
          <div className="flex items-center gap-4 text-sm text-muted-foreground mb-8">
            <span>{totalQuestions} questions</span>
            <span>{survey.estimated_time || "2 minutes"}</span>
            <span className="text-primary">{answeredCount}/{totalQuestions} answered</span>
          </div>
        </motion.div>

        <div className="space-y-6" data-testid="survey-form">
          {survey.questions.map((q, qi) => (
            <motion.div
              key={q.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: qi * 0.1 }}
              className="glass-card p-6"
              data-testid={`survey-question-${q.id}`}
            >
              <p className="text-white font-medium mb-4">{qi + 1}. {q.question}</p>
              <RadioGroup value={answers[q.id] || ""} onValueChange={(v) => setAnswers({ ...answers, [q.id]: v })}>
                <div className="space-y-2">
                  {q.options.map((opt, oi) => (
                    <div key={`${q.id}-${opt}`} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition-colors cursor-pointer" onClick={() => setAnswers({ ...answers, [q.id]: opt })}>
                      <RadioGroupItem value={opt} className="border-white/20 text-primary" data-testid={`survey-option-${q.id}-${oi}`} />
                      <Label className="text-white/70 cursor-pointer">{opt}</Label>
                    </div>
                  ))}
                </div>
              </RadioGroup>
            </motion.div>
          ))}
        </div>

        <div className="mt-8">
          <Button
            data-testid="submit-survey-btn"
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full h-14 rounded-full bg-primary text-primary-foreground text-lg font-medium btn-glow"
          >
            {submitting ? (
              <span className="flex items-center gap-2">
                <span className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                Submitting...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Send className="w-5 h-5" />
                Submit Response
              </span>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
